import React, { useState, useEffect, FormEvent } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useTranslation } from '../../../context/TranslationContext';
import { createPickupLocation, updatePickupLocation } from '../../../services/api';
import { PickupLocation } from '../../../types';
import './ProductModal.css'; // Reuse the same CSS

interface PickupLocationModalProps {
  location: PickupLocation | null;
  onClose: (locationUpdated?: boolean) => void;
}

const PickupLocationModal: React.FC<PickupLocationModalProps> = ({ location, onClose }) => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [formData, setFormData] = useState<Omit<PickupLocation, '_id'>>({
    name: '',
    address: '',
    openingHours: '',
    available: true
  });
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data if editing an existing location
  useEffect(() => {
    if (location) {
      setFormData({
        name: location.name,
        address: location.address,
        openingHours: location.openingHours,
        available: location.available
      });
    }
  }, [location]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const target = e.target as HTMLInputElement;
      setFormData(prev => ({
        ...prev,
        [name]: target.checked
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      if (location) {
        // Get location ID (either _id or id)
        const locationId = location._id || (location as any).id?.toString();
        
        // Validate location ID
        if (!locationId) {
          throw new Error('Pickup location ID is missing');
        }
        
        console.log('PickupLocationModal: Updating location with ID:', locationId);
        
        // Update existing location
        await updatePickupLocation(locationId, formData, token);
      } else {
        // Create new location
        await createPickupLocation(formData, token);
      }
      
      onClose(true); // Close modal and refresh location list
    } catch (err) {
      console.error('Error saving pickup location:', err);
      setError(t('admin.common.error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>{location ? t('admin.pickupLocations.editLocation') : t('admin.pickupLocations.addLocation')}</h2>
          <button className="modal-close-button" onClick={() => onClose()}>×</button>
        </div>
        
        <div className="modal-content">
          {error && <div className="error-message">{error}</div>}
          
          <form onSubmit={handleSubmit} className="product-form">
            <div className="form-group">
              <label htmlFor="name">{t('admin.pickupLocations.locationName')}</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="address">{t('admin.pickupLocations.address')}</label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="form-group">
              <label htmlFor="openingHours">{t('admin.pickupLocations.openingHours')}</label>
              <input
                type="text"
                id="openingHours"
                name="openingHours"
                value={formData.openingHours}
                onChange={handleChange}
                className="form-control"
                required
                placeholder="H-P: 9:00-17:00, Szo: 9:00-13:00"
              />
            </div>
            
            <div className="form-group checkbox-group">
              <input
                type="checkbox"
                id="available"
                name="available"
                checked={formData.available}
                onChange={handleChange}
              />
              <label htmlFor="available">{t('admin.pickupLocations.available')}</label>
            </div>
            
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => onClose()}
              >
                {t('admin.pickupLocations.cancel')}
              </button>
              <button 
                type="submit" 
                className="btn btn-primary" 
                disabled={loading}
              >
                {loading ? t('admin.common.loading') : t('admin.pickupLocations.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PickupLocationModal;
